'use client';

import { createContext, memo, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { useUAMP } from '@/hooks/useUAMP';
import { useT } from '@/components/i18n-provider';
// `suppressLinkPreviews` on both usages below, for the reason spelled out at
// the `Markdown` import in `tool-call-strip.tsx`: a bare URL in agent output
// would otherwise frame a third-party platform embed, and this preview renders
// inside the canvas, where an isolated document refuses one on every engine
// but Chromium.
import { Markdown } from '@/components/markdown';
import { ToolCallStrip, type ToolStripItem } from './tool-call-strip';

// Render depth context — used to short-circuit recursive sub-chat previews
// inside nested delegations (V1 keeps the preview one level deep per plan).
const PreviewDepthContext = createContext(0);
export const MAX_PREVIEW_DEPTH = 1;

/** Hook for callers (e.g. `getInteractiveResult`) to decide whether they
 *  should render a fresh `<DelegateSubChatPreview />` or fall back to the
 *  opaque text/headline-only path. */
export function usePreviewDepth(): number {
  return useContext(PreviewDepthContext);
}

// ---------------------------------------------------------------------------
// Types — kept locally (and intentionally narrow) so this preview can render
// off both DB rows (initial fetch) and live UAMP events without depending on
// the full chat-view Message shape.
// ---------------------------------------------------------------------------

interface ToolCallContentItem {
  type: 'tool_call';
  tool_call: { id: string; name: string; arguments: string };
}

interface ToolResultContentItem {
  type: 'tool_result';
  tool_result: { call_id: string; result: string; is_error?: boolean; data?: Record<string, unknown> };
}

interface TextContentItem { type: 'text'; text: string }
interface ThinkingContentItem { type: 'thinking'; text: string }

type ContentItem =
  | TextContentItem
  | ThinkingContentItem
  | ToolCallContentItem
  | ToolResultContentItem
  | { type: string; [k: string]: unknown };

interface PreviewMessage {
  id: string;
  chatId: string;
  senderUsername?: string;
  senderType?: 'human' | 'agent';
  role: 'user' | 'assistant' | 'system' | 'tool';
  content?: string;
  contentItems?: ContentItem[];
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Live overlay state for in-flight streaming
// ---------------------------------------------------------------------------

type StreamingItem =
  | { type: 'text'; text: string }
  | { type: 'tool_call'; id: string; name: string; arguments: string; result?: string; data?: Record<string, unknown> }
  | { type: 'thinking'; text: string };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SUB_TEXT_CLS =
  'text-xs text-foreground/70 dark:text-foreground/65 whitespace-pre-wrap break-words leading-relaxed';
const SUB_META_CLS = 'text-[11px] text-muted-foreground/60';
const ROLE_LABEL_CLS = 'text-[10px] uppercase tracking-wide text-muted-foreground/50 mr-1.5';

export const DelegateSubChatPreview = memo(function DelegateSubChatPreview({
  chatId,
}: { chatId: string }) {
  const t = useT();
  const depth = useContext(PreviewDepthContext);

  // V1: only render the preview at the top level. Nested delegations inside a
  // sub-chat collapse to a one-line hint so the user still sees that another
  // delegation happened without recursively expanding the tree (and without
  // the live WS subscription overhead per nested level). The early return
  // delegates the actual implementation to a child component to keep the
  // Rules-of-Hooks invariant — the heavy `useUAMP`/state hooks only run when
  // we actually intend to subscribe.
  if (depth >= MAX_PREVIEW_DEPTH) {
    return (
      <div className={cn(SUB_META_CLS, 'italic')}>
        {t('messages.nestedDelegation')}
      </div>
    );
  }

  return <DelegateSubChatPreviewInner chatId={chatId} depth={depth} />;
});

const DelegateSubChatPreviewInner = memo(function DelegateSubChatPreviewInner({
  chatId,
  depth,
}: { chatId: string; depth: number }) {
  const t = useT();
  const { isConnected, joinChat, leaveChat, on } = useUAMP();

  const [messages, setMessages] = useState<PreviewMessage[]>([]);
  const [overlay, setOverlay] = useState<Map<string, StreamingItem[]>>(() => new Map());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // -- initial + reconciliation fetch ---------------------------------------
  // Track whether the component is still mounted so we never set state after
  // unmount (e.g. user collapses the parent chat while a refetch is in
  // flight). We re-create the ref on chatId change so a stale fetch from a
  // previous chatId can't poison the new view either.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`/api/messages/${chatId}?limit=100`);
      if (!mountedRef.current) return;
      if (!res.ok) {
        if (res.status === 404) {
          setError(t('messages.subChatNotFound'));
          return;
        }
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      if (!mountedRef.current) return;
      const list: PreviewMessage[] = Array.isArray(data?.messages) ? data.messages : [];
      // Merge with anything already in local state so a slow GET doesn't
      // clobber a live `chat:message` that arrived first. Dedupe by id and
      // keep the chronologically-sorted order.
      setMessages((prev) => {
        const byId = new Map<string, PreviewMessage>();
        for (const m of list) byId.set(m.id, m);
        for (const m of prev) if (!byId.has(m.id)) byId.set(m.id, m);
        return Array.from(byId.values()).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      });
      setError(null);
    } catch (e) {
      if (!mountedRef.current) return;
      console.warn('[delegate-subchat-preview] fetch failed', e);
      setError(t('messages.subChatLoadFailed'));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [chatId, t]);

  // Reset on chatId change but DON'T blank the messages list — the WS
  // subscription effect re-runs in the same tick and any incoming events for
  // the new chatId should accumulate immediately. The fetch will merge into
  // whatever has already arrived.
  useEffect(() => {
    setLoading(true);
    setMessages([]);
    setError(null);
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId]);

  // -- WebSocket subscription ----------------------------------------------
  useEffect(() => {
    if (!isConnected || !chatId) return;
    joinChat(chatId);

    const unsubMessage = on('chat:message', (payload: unknown) => {
      const m = payload as PreviewMessage;
      if (m?.chatId !== chatId) return;
      setMessages((prev) => {
        if (prev.some((p) => p.id === m.id)) return prev;
        return [...prev, m].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      });
    });

    const unsubMessageUpdated = on('chat:message_updated', (payload: unknown) => {
      const m = payload as PreviewMessage;
      if (m?.chatId !== chatId) return;
      setMessages((prev) => prev.map((p) => (p.id === m.id ? { ...p, ...m } : p)));
    });

    const unsubMessageDeleted = on('chat:message_deleted', ({ chatId: deletedChatId, messageId }) => {
      if (deletedChatId !== chatId) return;
      setMessages((prev) => prev.filter((p) => p.id !== messageId));
    });

    const unsubDelta = on('response.delta', (data: unknown) => {
      const ev = data as {
        response_id?: string;
        delta?: { type?: string; text?: string; tool_call?: any; tool_result?: any; thinking?: { content?: string } };
        _portal?: { chatId?: string };
      };
      const portalChat = ev._portal?.chatId;
      if (portalChat && portalChat !== chatId) return;
      const responseId = ev.response_id;
      const delta = ev.delta;
      if (!responseId || !delta) return;
      setOverlay((prev) => {
        const next = new Map(prev);
        const items = [...(next.get(responseId) ?? [])];
        if (delta.type === 'text' && typeof delta.text === 'string') {
          const last = items[items.length - 1];
          if (last && last.type === 'text') {
            items[items.length - 1] = { type: 'text', text: last.text + delta.text };
          } else {
            items.push({ type: 'text', text: delta.text });
          }
        } else if (delta.type === 'thinking' && delta.thinking?.content) {
          const last = items[items.length - 1];
          if (last && last.type === 'thinking') {
            items[items.length - 1] = { type: 'thinking', text: last.text + delta.thinking.content };
          } else {
            items.push({ type: 'thinking', text: delta.thinking.content });
          }
        } else if (delta.type === 'tool_call' && delta.tool_call) {
          const tcId = delta.tool_call.id || `tc-${items.length}`;
          const existingIdx = items.findIndex((it) => it.type === 'tool_call' && it.id === tcId);
          if (existingIdx >= 0) {
            const ex = items[existingIdx] as Extract<StreamingItem, { type: 'tool_call' }>;
            items[existingIdx] = {
              ...ex,
              name: delta.tool_call.name || ex.name,
              arguments: delta.tool_call.arguments || ex.arguments,
            };
          } else {
            items.push({
              type: 'tool_call',
              id: tcId,
              name: delta.tool_call.name || 'unknown',
              arguments: delta.tool_call.arguments || '{}',
            });
          }
        } else if (delta.type === 'tool_result' && delta.tool_result) {
          const callId = delta.tool_result.call_id;
          const idx = items.findIndex((it) => it.type === 'tool_call' && it.id === callId);
          if (idx >= 0) {
            const ex = items[idx] as Extract<StreamingItem, { type: 'tool_call' }>;
            items[idx] = {
              ...ex,
              result: delta.tool_result.result,
              ...(delta.tool_result.data !== undefined ? { data: delta.tool_result.data as Record<string, unknown> } : {}),
            };
          }
        }
        next.set(responseId, items);
        return next;
      });
    });

    const unsubDone = on('response.done', (data: unknown) => {
      const ev = data as { response_id?: string; _portal?: { chatId?: string } };
      const portalChat = ev._portal?.chatId;
      if (portalChat && portalChat !== chatId) return;
      const responseId = ev.response_id;
      // Drop overlay for this response and refetch from DB so the bubble
      // becomes the canonical persisted version.
      setOverlay((prev) => {
        if (!responseId) return prev;
        if (!prev.has(responseId)) return prev;
        const next = new Map(prev);
        next.delete(responseId);
        return next;
      });
      fetchHistory();
    });

    const unsubError = on('response.error', (data: unknown) => {
      const ev = data as { _portal?: { chatId?: string } };
      const portalChat = ev._portal?.chatId;
      if (portalChat && portalChat !== chatId) return;
      // Force a reconcile on error too — DB will have any partial persisted
      // state plus the error marker row from persistDelegateOutput.
      fetchHistory();
    });

    return () => {
      unsubMessage();
      unsubMessageUpdated();
      unsubMessageDeleted();
      unsubDelta();
      unsubDone();
      unsubError();
      leaveChat(chatId);
    };
  }, [isConnected, chatId, joinChat, leaveChat, on, fetchHistory]);

  // -- render ---------------------------------------------------------------
  if (loading && messages.length === 0) {
    return (
      <div className={cn(SUB_META_CLS, 'italic')}>{t('messages.subChatLoading')}</div>
    );
  }
  if (error && messages.length === 0) {
    return <div className={cn(SUB_META_CLS, 'italic text-red-500/80')}>{error}</div>;
  }

  const overlayBuckets = Array.from(overlay.entries());

  return (
    <PreviewDepthContext.Provider value={depth + 1}>
      <div className="space-y-2 min-w-0 border-l border-border/40 pl-2">
        {messages.map((msg) => (
          <PreviewMessageRow key={msg.id} message={msg} />
        ))}
        {overlayBuckets.map(([responseId, items]) => (
          <PreviewStreamingRow key={`stream-${responseId}`} items={items} />
        ))}
      </div>
    </PreviewDepthContext.Provider>
  );
});

// ---------------------------------------------------------------------------
// Per-message row — collapses tool_call / tool_result pairs into a strip that
// reuses ToolCallStrip (same UI as main chat) while text/thinking content
// renders as muted/compact markdown.
// ---------------------------------------------------------------------------

function PreviewMessageRow({ message }: { message: PreviewMessage }) {
  const t = useT();
  const items = message.contentItems ?? (message.content ? [{ type: 'text' as const, text: message.content }] : []);

  // Build a tool strip from any tool_call/tool_result pairs, and collect
  // remaining text/thinking blocks separately (preserving order).
  const toolStrip: ToolStripItem[] = [];
  const inlineBlocks: Array<{ kind: 'text' | 'thinking'; text: string; key: string }> = [];

  items.forEach((it, i) => {
    if (it.type === 'tool_result') return; // folded into tool_call below
    if (it.type === 'tool_call' && 'tool_call' in it) {
      const tc = (it as ToolCallContentItem).tool_call;
      const resultItem = items.find(
        (r) => r.type === 'tool_result' && 'tool_result' in r && (r as ToolResultContentItem).tool_result.call_id === tc.id,
      ) as ToolResultContentItem | undefined;
      toolStrip.push({
        id: tc.id || `tc-${i}`,
        kind: 'tool',
        name: tc.name,
        args: tc.arguments,
        result: resultItem?.tool_result.result,
        isError: resultItem?.tool_result.is_error,
        data: resultItem?.tool_result.data,
      });
      return;
    }
    if (it.type === 'text' && typeof (it as TextContentItem).text === 'string') {
      inlineBlocks.push({ kind: 'text', text: (it as TextContentItem).text, key: `t-${i}` });
    } else if (it.type === 'thinking' && typeof (it as ThinkingContentItem).text === 'string') {
      inlineBlocks.push({ kind: 'thinking', text: (it as ThinkingContentItem).text, key: `th-${i}` });
    }
  });

  // role/sender label — keep tiny so the bubble reads at a glance
  const label = (() => {
    if (message.role === 'user') return t('messages.you');
    if (message.role === 'tool') return null; // tool rows already render inside the strip
    return message.senderUsername ? `@${message.senderUsername}` : t('messages.agentLabel');
  })();

  if (toolStrip.length === 0 && inlineBlocks.length === 0 && !message.content) return null;

  // Tool rows are folded into the assistant message above them; skip rendering standalone tool rows.
  if (message.role === 'tool') return null;

  return (
    <div className="min-w-0">
      {label && <span className={ROLE_LABEL_CLS}>{label}</span>}
      {inlineBlocks.map((b) =>
        b.kind === 'text' ? (
          <div key={b.key} className={cn(SUB_TEXT_CLS, 'prose prose-sm dark:prose-invert max-w-none prose-p:text-foreground/70 prose-p:my-1 min-w-0')}>
            <Markdown suppressLinkPreviews>{b.text}</Markdown>
          </div>
        ) : (
          <div key={b.key} className={cn(SUB_META_CLS, 'italic whitespace-pre-wrap break-words')}>
            {b.text}
          </div>
        ),
      )}
      {toolStrip.length > 0 && (
        <div className="mt-1">
          <ToolCallStrip items={toolStrip} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live streaming row — converts the StreamingItem buffer into a transient
// preview that mirrors a persisted message row (text + tool strip).
// ---------------------------------------------------------------------------

function PreviewStreamingRow({ items }: { items: StreamingItem[] }) {
  const t = useT();
  const toolStrip: ToolStripItem[] = [];
  const inlineBlocks: Array<{ kind: 'text' | 'thinking'; text: string; key: string }> = [];

  items.forEach((it, i) => {
    if (it.type === 'text') inlineBlocks.push({ kind: 'text', text: it.text, key: `s-t-${i}` });
    else if (it.type === 'thinking') inlineBlocks.push({ kind: 'thinking', text: it.text, key: `s-th-${i}` });
    else if (it.type === 'tool_call') {
      toolStrip.push({
        id: it.id,
        kind: 'tool',
        name: it.name,
        args: it.arguments,
        result: it.result,
        data: it.data,
        isStreaming: it.result === undefined,
      });
    }
  });

  if (toolStrip.length === 0 && inlineBlocks.length === 0) return null;

  return (
    <div className="min-w-0">
      <span className={ROLE_LABEL_CLS}>{t('messages.agentLabel')}</span>
      {inlineBlocks.map((b) =>
        b.kind === 'text' ? (
          <div key={b.key} className={cn(SUB_TEXT_CLS, 'prose prose-sm dark:prose-invert max-w-none prose-p:text-foreground/70 prose-p:my-1 min-w-0')}>
            <Markdown suppressLinkPreviews>{b.text}</Markdown>
          </div>
        ) : (
          <div key={b.key} className={cn(SUB_META_CLS, 'italic whitespace-pre-wrap break-words')}>
            {b.text}
          </div>
        ),
      )}
      {toolStrip.length > 0 && (
        <div className="mt-1">
          <ToolCallStrip items={toolStrip} />
        </div>
      )}
    </div>
  );
}
